/**
 * Block interaction: a DDA voxel raycast over `ClientWorld.blockAt` plus the hotbar palette.
 *
 * The raycast is Amanatides & Woo: it walks voxel boundaries in order of increasing `t`, so the FIRST
 * solid voxel along the view ray is the one the crosshair is on, and the axis that stepped last is the
 * hit FACE normal used for placement. Everything is integer-stepping plus a few `Math.floor`/division
 * operations; the caller passes a reusable `RayHit` so the interaction path allocates nothing.
 */

import { VOXEL_SIZE_M, type BlockId, type Vec3 } from "@rogueworlds/shared";
import { AIR, DIRT, GLASS, GRASS, LEAVES, LOG, PLANK, SAND, SNOW, STONE } from "./bridge";

/** Maximum reach in meters. */
export const REACH_M = 6;

/**
 * Hotbar contents: nine placeable blocks from the voxel registry, in ascending id order (every
 * non-air block except WATER, which is not solid enough to place as a wall, and BASALT, which exists
 * only as the world floor below y = 0). The ids are the registry's own ids - the client never invents
 * a block, and `1`..`9` select slots `0`..`8`.
 */
export const HOTBAR_BLOCKS: readonly BlockId[] = [
  STONE,
  DIRT,
  GRASS,
  SAND,
  LOG,
  LEAVES,
  PLANK,
  GLASS,
  SNOW,
];

export interface RayHit {
  bx: number;
  by: number;
  bz: number;
  /** Face normal of the hit voxel (one axis is +/-1, the others 0). */
  nx: number;
  ny: number;
  nz: number;
  block: BlockId;
  distance: number;
}

export interface VoxelQuery {
  blockAt(bx: number, by: number, bz: number): BlockId;
}

/**
 * Casts from `origin` (meters, world space) along `direction` (need not be normalized) and returns the
 * first non-air voxel within `REACH_M`, or `undefined`. `out` is filled in place when it is hit.
 */
export function raycastVoxels(
  query: VoxelQuery,
  origin: Vec3,
  direction: Vec3,
  out: RayHit,
  maxDistance: number = REACH_M,
): RayHit | undefined {
  const dirX = direction.x;
  const dirY = direction.y;
  const dirZ = direction.z;
  if (dirX === 0 && dirY === 0 && dirZ === 0) {
    return undefined;
  }
  let vx = Math.floor(origin.x / VOXEL_SIZE_M);
  let vy = Math.floor(origin.y / VOXEL_SIZE_M);
  let vz = Math.floor(origin.z / VOXEL_SIZE_M);

  const stepX = dirX > 0 ? 1 : dirX < 0 ? -1 : 0;
  const stepY = dirY > 0 ? 1 : dirY < 0 ? -1 : 0;
  const stepZ = dirZ > 0 ? 1 : dirZ < 0 ? -1 : 0;

  // Distance along the ray between consecutive voxel boundaries on each axis (infinity when parallel).
  const deltaX = stepX === 0 ? Number.POSITIVE_INFINITY : VOXEL_SIZE_M / Math.abs(dirX);
  const deltaY = stepY === 0 ? Number.POSITIVE_INFINITY : VOXEL_SIZE_M / Math.abs(dirY);
  const deltaZ = stepZ === 0 ? Number.POSITIVE_INFINITY : VOXEL_SIZE_M / Math.abs(dirZ);

  // Distance to the first boundary on each axis.
  let maxX = boundaryDistance(origin.x, vx, stepX, dirX);
  let maxY = boundaryDistance(origin.y, vy, stepY, dirY);
  let maxZ = boundaryDistance(origin.z, vz, stepZ, dirZ);

  let normalX = 0;
  let normalY = 0;
  let normalZ = 0;
  let travelled = 0;

  // Bounded: at most ~3 voxels of walking per 0.1 m of reach plus a hard iteration cap.
  const maxSteps = Math.ceil(maxDistance / VOXEL_SIZE_M) * 3 + 3;
  for (let step = 0; step < maxSteps; step++) {
    const block = query.blockAt(vx, vy, vz);
    if (block !== AIR) {
      out.bx = vx;
      out.by = vy;
      out.bz = vz;
      out.nx = normalX;
      out.ny = normalY;
      out.nz = normalZ;
      out.block = block;
      out.distance = travelled;
      return out;
    }
    if (maxX <= maxY && maxX <= maxZ) {
      travelled = maxX;
      if (travelled > maxDistance) {
        return undefined;
      }
      vx += stepX;
      maxX += deltaX;
      normalX = -stepX;
      normalY = 0;
      normalZ = 0;
    } else if (maxY <= maxZ) {
      travelled = maxY;
      if (travelled > maxDistance) {
        return undefined;
      }
      vy += stepY;
      maxY += deltaY;
      normalX = 0;
      normalY = -stepY;
      normalZ = 0;
    } else {
      travelled = maxZ;
      if (travelled > maxDistance) {
        return undefined;
      }
      vz += stepZ;
      maxZ += deltaZ;
      normalX = 0;
      normalY = 0;
      normalZ = -stepZ;
    }
  }
  return undefined;
}

/** Distance from an axis origin to the next voxel boundary in the step direction. */
function boundaryDistance(origin: number, voxel: number, step: number, dir: number): number {
  if (step === 0) {
    return Number.POSITIVE_INFINITY;
  }
  const boundary = step > 0 ? (voxel + 1) * VOXEL_SIZE_M : voxel * VOXEL_SIZE_M;
  return (boundary - origin) / dir;
}
